import React, { useState } from 'react';
import {
  Typography,
  Alert,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
} from '@mui/material';
import {
  HelpOutline as HelpOutlineIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import SeverancesCharts from '../charts/SeverancesCharts';
import FullScreenTable from '../tables/FullScreenTable';
import UpgradeRequestDialog from '../UpgradeRequestDialog';
import logger from '../../utils/logger';

const SeverancesTab = ({
  searchData,
  getCurrentTableCounts,
  getCurrentTableData,
  getCurrentTableType,
  severancesTableSort,
  handleSeverancesTableSort,
  resetSeverancesTableSort,
  severancesFilter,
  setSeverancesFilter,
  resetSeverancesFilter,
  hiddenSeverancesLines,
  setHiddenSeverancesLines,
  resetHiddenSeverancesLines,
  hiddenSeveranceVolumeLines,
  setHiddenSeveranceVolumeLines,
  resetHiddenSeveranceVolumeLines,
  tierInfo,
}) => {
  // Local state for operator names popup
  const [operatorNamesDialogOpen, setOperatorNamesDialogOpen] = useState(false);
  const [fullScreenTableOpen, setFullScreenTableOpen] = useState(false);
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  // Local state for upgrade dialog
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  
  // Don't render if not severances tab or no data
  if (getCurrentTableType() !== 'severances' || !getCurrentTableData() || getCurrentTableData().length === 0) {
    return null;
  }

  const severancesData = getCurrentTableData();
  
  // Calculate unique operator names for this tab only
  const getTabOperatorNames = () => {
    if (!severancesData || severancesData.length === 0) return [];
    
    const operatorNames = new Set();
    severancesData.forEach(record => {
      const operator = record.operator_name || record['Operator Name'] || record.operator;
      if (operator && operator.toString().trim() !== '') {
        operatorNames.add(operator.toString().trim());
      }
    });
    
    return Array.from(operatorNames).sort();
  };
  
  // Calculate total violation letters
  const totalViolationLetters = severancesData.length;
  
  // Calculate date range
  const dateColumn = severancesData[0]['Certified Letter Date'] !== undefined ? 'Certified Letter Date' :
                   severancesData[0].certified_letter_date !== undefined ? 'certified_letter_date' : null;
  
  let dateRangeText = '';
  if (dateColumn) {
    const dates = severancesData
      .map(record => record[dateColumn])
      .filter(date => date)
      .map(date => new Date(date))
      .filter(date => !isNaN(date.getTime()));
    
    if (dates.length > 0) {
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      
      const formatDate = (date) => {
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      };
      
      // Check if dates are in the same month/year
      if (minDate.getFullYear() === maxDate.getFullYear() && minDate.getMonth() === maxDate.getMonth()) {
        dateRangeText = ` (${formatDate(minDate)})`;
      } else {
        dateRangeText = `, with occurances from ${formatDate(minDate)} to ${formatDate(maxDate)}`;
      }
    }
  }
  
  // Calculate resolved violations based on "Violation Resolved Date"
  const resolvedDateColumn = severancesData[0]['Violation Resolved Date'] !== undefined ? 'Violation Resolved Date' :
                           severancesData[0].violation_resolved_date !== undefined ? 'violation_resolved_date' : null;
  
  let resolvedCount = 0;
  let resolvedRecords = [];
  if (resolvedDateColumn) {
    resolvedRecords = severancesData.filter(record => {
      const resolvedDate = record[resolvedDateColumn];
      return resolvedDate && resolvedDate.toString().trim() !== '';
    });
    resolvedCount = resolvedRecords.length;
  }
  
  // Calculate resolution percentage with proper formatting
  const resolutionPercentage = totalViolationLetters > 0 ? ((resolvedCount / totalViolationLetters) * 100).toFixed(1) : 0;
  
  // Calculate average days to resolve (Certified Letter Date to Violation Resolved Date)
  let averageDaysToResolve = 0;
  if (resolvedRecords.length > 0 && dateColumn) {
    const validDays = resolvedRecords
      .map(record => {
        const certifiedDate = new Date(record[dateColumn]);
        const resolvedDate = new Date(record[resolvedDateColumn]);
        
        if (!isNaN(certifiedDate.getTime()) && !isNaN(resolvedDate.getTime())) {
          const timeDiff = resolvedDate.getTime() - certifiedDate.getTime();
          return timeDiff / (1000 * 60 * 60 * 24); // Convert to days
        }
        return null;
      })
      .filter(days => days !== null && days >= 0);
    
    if (validDays.length > 0) {
      averageDaysToResolve = Math.round(validDays.reduce((sum, days) => sum + days, 0) / validDays.length);
    }
  }
  
  // Calculate unresolved violations (those without Violation Resolved Date)
  const unresolvedRecords = severancesData.filter(record => {
    const resolvedDate = record[resolvedDateColumn];
    return !resolvedDate || resolvedDate.toString().trim() === '';
  });
  const unresolvedCount = unresolvedRecords.length;
  
  // Calculate severance/seal orders for unresolved (those with Issue Date)
  const issueDateColumn = severancesData[0]['Issue Date'] !== undefined ? 'Issue Date' :
                        severancesData[0].issue_date !== undefined ? 'issue_date' : null;
  
  let severanceOrderCount = 0;
  if (issueDateColumn) {
    severanceOrderCount = unresolvedRecords.filter(record => {
      const issueDate = record[issueDateColumn];
      return issueDate && issueDate.toString().trim() !== '';
    }).length;
  }
  
  // Calculate severance order percentage for unresolved
  const severanceOrderPercentage = unresolvedCount > 0 ? (severanceOrderCount / unresolvedCount) * 100 : 0;
  
  // Calculate leading cause of certified letter issuance (Issue Reason)
  const issueReasonColumn = severancesData[0]['Issue Reason'] !== undefined ? 'Issue Reason' :
                          severancesData[0].issue_reason !== undefined ? 'issue_reason' : null;
  
  let leadingCause = '';
  let leadingCausePercentage = 0;
  
  if (issueReasonColumn) {
    const reasonCounts = {};
    severancesData.forEach(record => {
      const reason = record[issueReasonColumn];
      if (reason && reason.toString().trim() !== '') {
        const reasonKey = reason.toString().trim();
        reasonCounts[reasonKey] = (reasonCounts[reasonKey] || 0) + 1;
      }
    });
    
    // Find the most common reason
    const entries = Object.entries(reasonCounts);
    if (entries.length > 0) {
      const sortedEntries = entries.sort((a, b) => b[1] - a[1]);
      leadingCause = sortedEntries[0][0];
      leadingCausePercentage = ((sortedEntries[0][1] / totalViolationLetters) * 100).toFixed(1);
    }
  }

  return (
    <>
      {/* Severances Findings Card */}
      <Card elevation={0} sx={{ mb: 3, border: 1, borderColor: 'divider' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h5" component="h2">
              Severances
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
              <Tooltip title="About Severances" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setAboutDialogOpen(true)}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Raw Data" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setFullScreenTableOpen(true)}
                  disabled={tierInfo && !tierInfo.can_view_raw_data}
                  sx={{
                    opacity: (tierInfo && !tierInfo.can_view_raw_data) ? 0.5 : 1,
                    cursor: (tierInfo && !tierInfo.can_view_raw_data) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <TableChartIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Content */}
          <Box>
            {/* Unique Operator Names */}
            <Box sx={{ mb: 2, textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>
                  Unique Operator Names — {getTabOperatorNames().length.toLocaleString()}
                </Typography>
                <Tooltip title="View all unique operator names" arrow>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setOperatorNamesDialogOpen(true)}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            {/* Divider */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, mt: 2 }} />
            
            {/* Findings Content */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
                The severances data review process discovered{' '}
                <strong>{totalViolationLetters.toLocaleString()}</strong> total issued violation letter{totalViolationLetters !== 1 ? 's' : ''} associated with the subject assets{dateRangeText}.
              </Typography>

              <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
                Of the violation letters issued,{' '}
                <strong>{resolutionPercentage}%</strong> were resolved{resolvedCount > 0 ? (
                  <>
                    {' '}at an average of <strong>{Math.round(averageDaysToResolve)}</strong> days to resolve
                  </>
                ) : ''}.
              </Typography>

              <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
                By count,{' '}
                <strong>{unresolvedCount.toLocaleString()}</strong> of the violations remained unresolved{unresolvedCount > 0 ? (
                  <>
                    , of which <strong>{severanceOrderPercentage.toFixed(1)}%</strong> had issued severance or seal orders
                  </>
                ) : ''}.
              </Typography>

              {/* Leading Cause of Certified Letter Issuance */}
              {leadingCause && (
                <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
                  <strong>{leadingCause}</strong> was identified as the leading cause of certified letter issuance, accounting for <strong>{leadingCausePercentage}%</strong> of the total.
                </Typography>
              )}

              {/* Analytics Scope Notice */}
              {getCurrentTableData().length < getCurrentTableCounts() && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> Analytics are based on {getCurrentTableData().length.toLocaleString()} of {getCurrentTableCounts().toLocaleString()} identified records 
                    (limited to first {getCurrentTableData().length.toLocaleString()} rows).
                  </Typography>
                </Alert>
              )}
            </Box>
          </Box>
          
          {tierInfo && !tierInfo.can_view_raw_data && (
            <Typography variant="body2" color="primary.main" sx={{ 
              mt: 2, 
              mb: 1, 
              fontWeight: 500 
            }}>
              💡 <button 
                onClick={() => setUpgradeDialogOpen(true)}
                style={{ 
                  color: 'inherit', 
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Upgrade to Pro to view raw data tables
              </button>
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Severances Charts */}
      <SeverancesCharts
        severancesData={getCurrentTableData()}
        onSortUnresolvedViolations={handleSeverancesTableSort}
        unresolvedViolationsSortField={severancesTableSort.field}
        unresolvedViolationsSortDirection={severancesTableSort.direction}
        onResetUnresolvedViolations={resetSeverancesTableSort}
        severancesFilter={severancesFilter}
        setSeverancesFilter={setSeverancesFilter}
        resetSeverancesFilter={resetSeverancesFilter}
        hiddenSeverancesLines={hiddenSeverancesLines}
        setHiddenSeverancesLines={setHiddenSeverancesLines}
        resetHiddenSeverancesLines={resetHiddenSeverancesLines}
        hiddenSeveranceVolumeLines={hiddenSeveranceVolumeLines}
        setHiddenSeveranceVolumeLines={setHiddenSeveranceVolumeLines}
        resetHiddenSeveranceVolumeLines={resetHiddenSeveranceVolumeLines}
      />

      {/* About Dialog */}
      <Dialog
        open={aboutDialogOpen}
        onClose={() => setAboutDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          About Severances
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography paragraph>
              On identification of a rule violation, state regulators send a notice to the operator by certified mail. The notice advises the operator of the violation and provides a deadline for correction (generally, 30 days). The letter also advises the operator that if the violation isn't corrected by the deadline, a severance (for oil leases) or seal order (for gas wells) will be issued prohibiting the operator from producing, moving oil or gas, or otherwise using the well(s) affected.
            </Typography>
            <Typography paragraph sx={{ mt: 3 }}>
              <strong>Compliance status definitions</strong>
            </Typography>
            <Typography paragraph>
              <strong>Resolved Without Severance/Seal Order</strong> – If the violation is corrected prior to the deadline given in the notice, no further action is taken and the matter is closed.
            </Typography>
            <Typography paragraph>
              <strong>Resolved After Severance/Seal Order</strong> – After the deadline has passed, a severance or seal order is issued. The order prohibits the operator from producing, selling hydrocarbons, or otherwise using any of the wells under the order until the violation has been corrected and a statutory fee has been paid.
            </Typography>
            <Typography paragraph>
              <strong>Unresolved Notices</strong> – Indicates violations that have not yet been resolved. This includes matters where the deadline contained in the notice has not yet passed, and matters where severance/seal orders are in place but corrective action by the operator is still required. In cases where the severance/seal order has been issued, state regulators may proceed (or may have already proceeded) to further enforcement action.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAboutDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Operator Names Dialog */}
      <Dialog
        open={operatorNamesDialogOpen}
        onClose={() => setOperatorNamesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Unique Operator Names
            </Typography>
            <IconButton
              onClick={() => setOperatorNamesDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing unique operator names from current search results ({getCurrentTableData().length.toLocaleString()} records)
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {getTabOperatorNames().length > 0 ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Total: {getTabOperatorNames().length.toLocaleString()} unique operators</strong>
                </Typography>
                <List dense>
                  {getTabOperatorNames().map((operator, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText primary={operator} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : (
              <Typography>No operator data available</Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Full Screen Table Dialog */}
      <FullScreenTable
        open={fullScreenTableOpen}
        onClose={() => setFullScreenTableOpen(false)}
        tableType="severances"
        data={severancesData}
        allColumns={severancesData.length > 0 ? Object.keys(severancesData[0]) : []}
        defaultColumns={['operator_name', 'lease_name', 'certified_letter_date', 'violation_resolved_date', 'issue_reason']}
        totalCount={getCurrentTableCounts()}
        rowLimit={tierInfo?.table_limits?.severances || 0}
        onReset={() => {
          // Reset any severances-specific state if needed
          logger.debug('Severances table reset');
        }}
      />
      
      {/* Upgrade Request Dialog */}
      <UpgradeRequestDialog 
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
      />
    </>
  );
};

export default SeverancesTab;